import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Flex,
  Heading,
  Text,
} from '@chakra-ui/react'
import { useState } from 'react'
import { Link, Navigate, useLocation } from 'react-router-dom'
import SimpleBar from 'simplebar-react'

import { Icon } from '../Icons/Icon'
import { IconTypes } from '../Icons/icon-registry'
import { PageLayout } from '../PageLayout/PageLayout'

const pages = [
  {
    label: 'Auth',
    children: [
      {
        label: 'Organizations',
        path: '/admin/organizations',
      },
      {
        label: 'Users',
        path: '/admin/users',
      },
    ],
  },
  {
    label: 'Customer Data API',
    children: [
      {
        label: 'Customer Data API Status',
        path: '/admin/customer-data-api-status',
      },
      {
        label: 'Customer Data API Config',
        path: '/admin/customer-data-api-config',
      },
      {
        label: 'GraphQL Gateway',
        path: '/admin/graphql-gateway',
      },
      {
        label: 'Deployments',
        path: '/admin/deployments',
      },
      {
        label: 'SMRT Artifacts',
        path: '/admin/smrt-artifacts',
      },
      {
        label: 'Audit Log',
        path: '/admin/audit',
      },
    ],
  },
]

interface AdminPageLayoutProps {
  pageTitle: string
  icon?: IconTypes
  children: React.ReactNode
}

export const AdminPageLayout = ({
  pageTitle,
  icon,
  children,
}: AdminPageLayoutProps) => {
  const [expandedIndexes, setExpandedIndexes] = useState<number[]>(() =>
    pages.map((_, i) => i),
  )
  const location = useLocation()

  const allPages = [...pages.map(({ children }) => children).flat()]

  const isCurrentPage = (path: string) =>
    location.pathname === path ||
    (path === '/admin/customer-data-api-config' &&
      (location.pathname.startsWith('/admin/customer-data-api-config/') ||
        location.pathname === '/admin/customer-data-api-config')) ||
    (path === '/admin/deployments' &&
      location.pathname.startsWith('/admin/deployments')) ||
    (path === '/admin/organizations' &&
      location.pathname.startsWith('/admin/organizations')) ||
    (path === '/admin/users' && location.pathname.startsWith('/admin/users'))

  const isDisabled = (path: string) =>
    allPages.find(page => page.path === path)?.path === '#'

  if (location.pathname === '/admin') {
    return <Navigate to="/admin/customer-data-api-status" />
  }

  return (
    <PageLayout
      documentTitle={pageTitle}
      headerSlot={
        <Flex alignItems="center" gap="8px">
          <Icon
            icon={icon || 'GearIcon'}
            boxSize="26px"
            color="presets.neutral.90"
          />
          <Heading variant="pageTitle" mt="0" data-testid="admin-page-heading">
            {pageTitle}
          </Heading>
        </Flex>
      }
      sidebarSlot={
        <Flex
          flexDirection="column"
          height="full"
          width="238px"
          bg="presets.neutral.10"
        >
          <Text
            fontSize="12px"
            fontWeight="600"
            color="presets.turquoise.100"
            textTransform="uppercase"
            pl="12px"
            py="4px"
            borderBottom="1px solid"
            borderColor="presets.neutral.20"
          >
            Admin
          </Text>
          <SimpleBar
            style={{
              maxHeight: '100%',
              width: '100%',
            }}
          >
            <Accordion
              key="admin-accordion"
              index={expandedIndexes}
              onChange={index => {
                setExpandedIndexes(Array.isArray(index) ? index : [index])
              }}
              allowMultiple
            >
              {pages.map((page, i) => (
                <AccordionItem key={i} border="none">
                  <AccordionButton justifyContent="space-between" px="12px">
                    <Text
                      color="presets.neutral.80"
                      textTransform="uppercase"
                      fontSize="12px"
                      fontWeight="700"
                    >
                      {page.label}
                    </Text>
                    <AccordionIcon
                      color="presets.neutral.50"
                      // no transitions to work around state issues with accordion chevrons.
                      sx={{
                        transition: 'none !important',
                        transform: 'none !important',
                      }}
                    />
                  </AccordionButton>
                  <AccordionPanel p="0" mb="8px">
                    {page.children.map((child, i) => (
                      <Flex
                        as={Link}
                        to={child.path}
                        key={i}
                        alignItems="center"
                        gap="8px"
                        py="8px"
                        px="12px"
                        transition="all 0.2s ease-in-out"
                        _hover={{
                          bg: isDisabled(child.path)
                            ? 'transparent'
                            : isCurrentPage(child.path)
                              ? 'presets.turquoise.70'
                              : 'presets.neutral.20',
                        }}
                        bg={
                          isCurrentPage(child.path)
                            ? 'presets.turquoise.50'
                            : 'transparent'
                        }
                        cursor={
                          isDisabled(child.path) ? 'not-allowed' : 'pointer'
                        }
                      >
                        <Icon
                          icon={icon || 'GearIcon'}
                          boxSize="16px"
                          color={
                            isCurrentPage(child.path)
                              ? 'presets.neutral.10'
                              : 'presets.neutral.50'
                          }
                        />
                        <Text
                          key={i}
                          opacity={isDisabled(child.path) ? 0.5 : 1}
                          fontStyle={
                            isDisabled(child.path) ? 'italic' : 'normal'
                          }
                          lineHeight="16px"
                          color={
                            isCurrentPage(child.path)
                              ? 'presets.neutral.10'
                              : 'presets.neutral.90'
                          }
                          _hover={{
                            color: isCurrentPage(child.path)
                              ? 'presets.neutral.10'
                              : 'presets.neutral.90',
                          }}
                        >
                          {child.label}
                        </Text>
                      </Flex>
                    ))}
                  </AccordionPanel>
                </AccordionItem>
              ))}
            </Accordion>
          </SimpleBar>
        </Flex>
      }
    >
      {children}
    </PageLayout>
  )
}
